import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Message } from "@ag-ui/client";
import { derivePlan, isPlanTool, type PlanTask } from "./derivePlan";
import { useConversationPlan } from "./useConversationPlan";
import type { ToolCallEntry } from "./useTurns";

interface SessionPlanContextValue {
  plan: PlanTask[];
  // Whether the agent is currently running. Threaded through so the sidebar —
  // a sibling of `Chat`, with no access to the agent — can stop the checklist's
  // in-progress spinner once a turn ends.
  isRunning: boolean;
  setPlan: (plan: PlanTask[]) => void;
  setIsRunning: (isRunning: boolean) => void;
}

// Holds the session's current plan above both `Chat` (which owns the messages
// it is derived from) and `SessionContextSidebar` (which renders it) — the two
// are siblings, so the shared state lives in their common ancestor. Mirrors
// `SessionEvalRunsContext` / `SessionIngestsContext`.
const SessionPlanContext = createContext<SessionPlanContextValue | null>(null);

export const SessionPlanProvider = ({
  conversationId,
  children,
}: {
  // The active conversation, so the shared plan resets when the user switches
  // sessions. Optional: non-conversation mounts (and unit tests) pass none.
  conversationId?: string;
  children: React.ReactNode;
}) => {
  const [plan, setPlan] = useState<PlanTask[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  // Clear the shared plan whenever the active conversation changes, so a plan
  // published for one session — surfaced in the sidebar's Apps > Plan entry and
  // its detail view — does not linger over the next. `Chat` republishes the new
  // session's plan, but its `Conversation` is remounted by `key={conversationId}`
  // (see `ConversationRoute`), so there is a gap while the fresh `Chat` mounts
  // and reconnects; without this reset the sidebar shows the prior plan for that
  // gap. Reset while rendering — the React idiom, mirroring
  // `SessionContextPanelProvider` — not with an effect (disallowed for resetting
  // state) or a `key` (which would remount this provider's whole subtree).
  const [prevConversationId, setPrevConversationId] = useState(conversationId);
  if (conversationId !== prevConversationId) {
    setPrevConversationId(conversationId);
    setPlan([]);
    setIsRunning(false);
  }

  return (
    <SessionPlanContext.Provider value={{ plan, isRunning, setPlan, setIsRunning }}>
      {children}
    </SessionPlanContext.Provider>
  );
};

// The conversation's current plan is a pure function of history: fold every
// plan-tool call across all turns, in order. `write_plan` is a whole-plan
// replacement, so a later turn's write_plan supersedes an earlier one and the
// fold converges on the most recent plan; a turn with no plan calls leaves the
// last plan standing. Results are paired to their calls by tool_call_id — the
// same correlation `useTurns` performs per turn.
export function sessionPlanFromMessages(messages: ReadonlyArray<Message>): PlanTask[] {
  const resultByCallId = new Map<string, string>();
  for (const m of messages) {
    const tm = m as { role?: string; toolCallId?: string; content?: string };
    if (tm.role === "tool" && tm.toolCallId && typeof tm.content === "string") {
      resultByCallId.set(tm.toolCallId, tm.content);
    }
  }

  const entries: ToolCallEntry[] = [];
  for (const m of messages) {
    const calls = (
      m as { toolCalls?: { id: string; function: { name: string; arguments: string } }[] }
    ).toolCalls;
    if (!calls) continue;
    for (const tc of calls) {
      if (!isPlanTool(tc.function.name)) continue;
      entries.push({
        id: tc.id,
        name: tc.function.name,
        args: tc.function.arguments,
        result: resultByCallId.get(tc.id),
      });
    }
  }
  return derivePlan(entries);
}

// Chooses the plan to surface from the two sources. The durable store is
// authoritative and pagination-independent; the message fold is the responsive
// view while the agent streams plan-tool calls. So mid-turn the fold wins (it
// updates live, the store would need re-reading per call), and when idle the
// store wins — falling back to the fold when the store is empty or unreachable
// (a Postgres-less local stack), so a plan always shows if either source has
// one.
export function chooseSessionPlan(
  isRunning: boolean,
  foldPlan: PlanTask[],
  durablePlan: PlanTask[]
): PlanTask[] {
  if (isRunning) return foldPlan;
  return durablePlan.length > 0 ? durablePlan : foldPlan;
}

// Called from `Chat`. Publishes the current plan and the agent's running state
// into the shared context so the sibling sidebar can render them. The plan is
// the durable store's when idle, the live message fold while a turn runs (see
// `chooseSessionPlan`). `hasPlan` gates the durable read so it fires only when
// the conversation has a plan (see `conversationPlanKey`). Ambient: no auto-open —
// it must not steal the panel's focus the way a launched eval run does.
export function useSessionPlanSync(
  messages: ReadonlyArray<Message>,
  isRunning: boolean,
  conversationId: string | undefined,
  hasPlan: boolean
): void {
  const ctx = useContext(SessionPlanContext);
  const foldPlan = useMemo(() => sessionPlanFromMessages(messages), [messages]);
  const durablePlan = useConversationPlan(conversationId, isRunning, hasPlan);
  const plan = useMemo(
    () => chooseSessionPlan(isRunning, foldPlan, durablePlan),
    [isRunning, foldPlan, durablePlan]
  );

  useEffect(() => {
    ctx?.setPlan(plan);
    ctx?.setIsRunning(isRunning);
  }, [ctx, plan, isRunning]);
}

// Read-only projection of the session's current plan and whether the agent is
// running — what the sidebar's Plan entry and detail view render from.
export function useSessionPlan(): { plan: PlanTask[]; isRunning: boolean } {
  const ctx = useContext(SessionPlanContext);
  if (!ctx) {
    throw new Error("useSessionPlan must be used within a SessionPlanProvider");
  }
  return { plan: ctx.plan, isRunning: ctx.isRunning };
}
